import { QuestionItemAnswer } from '@app/src/components/Table/Cells/AnswerWithFlagCell'
import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import { getSxByRank } from '@app/src/pages/QuestionEditor/AnswerClassification/ClassificationRow'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { QuestionAnswerRank } from '@app/src/types/resourceExplorer'
import { AutoAwesomeOutlined, Check, Close, Remove, ReportGmailerrorred } from '@mui/icons-material'
import { alpha, AvatarGroup, Stack, SvgIcon, Tooltip, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { DRAWER_WIDTH, useDrawer } from '../../Drawer/DrawerContext'
import DrawerViewAiScreenedAnswerDetail from '../../Drawer/Views/DrawerViewAiScreenedAnswerDetail'
import ParentCellOrFragment from './ParentCellOrFragment'

type AnswerWithClassificationCellProps = { item: ResponseItem; disableCell?: boolean }

const rankIconTypes = {
  [QuestionAnswerRank.NotSet]: Remove,
  [QuestionAnswerRank.Neutral]: Remove,
  [QuestionAnswerRank.Positive]: Check,
  [QuestionAnswerRank.VeryPositive]: Check,
  [QuestionAnswerRank.Negative]: Close,
  [QuestionAnswerRank.NegativeAdverse]: ReportGmailerrorred,
}

export const RANK_TYPE_ORDER = [
  QuestionAnswerRank.VeryPositive,
  QuestionAnswerRank.Positive,
  QuestionAnswerRank.NegativeAdverse,
  QuestionAnswerRank.Negative,
  QuestionAnswerRank.Neutral,
  QuestionAnswerRank.NotSet,
]

const AnswerWithClassificationCell: React.FC<AnswerWithClassificationCellProps> = ({ item, disableCell }) => {
  const { palette } = useTheme()
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const ranks = useMemo(
    () => Array.from(new Set(item.questionAnswerClassificationResults?.map(q => q.rank) ?? [])),
    [item.questionAnswerClassificationResults],
  )

  const tooltipText = useMemo(
    () =>
      ranks
        ?.sort((a, b) => RANK_TYPE_ORDER.indexOf(a) - RANK_TYPE_ORDER.indexOf(b))
        .map(x =>
          formatMessage({
            id: `templateBuilder.answerClassifications.classificationsRanks.${x}`,
          }),
        )
        .join(', '),
    [ranks],
  )

  return (
    <ParentCellOrFragment disableCell={disableCell}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={({ palette }) => ({
          height: '100%',
          width: '100%',
          cursor: item?.isScraped === true ? 'pointer' : 'default',
          '&:hover': item?.isScraped === true ? { backgroundColor: palette.communication.light } : {},
        })}
        onClick={() => {
          if (item?.isScraped === true) {
            openDrawer(<DrawerViewAiScreenedAnswerDetail item={item} />, {
              showAboveDialogs: true,
              maxWidth: DRAWER_WIDTH.wide,
            })
          }
        }}
      >
        {Boolean(ranks?.length) && (
          <Tooltip arrow title={tooltipText}>
            <AvatarGroup
              sx={{
                marginLeft: '-2px',
                '& .MuiAvatar-root': {
                  marginLeft: '-16px',
                },
              }}
            >
              {ranks?.map(rank => (
                <Avatar key={rank} size={AvatarSize.Small} sx={{ bgcolor: getSxByRank(palette, rank).bgcolor }}>
                  <SvgIcon
                    fontSize="small"
                    component={rankIconTypes[rank]}
                    sx={{
                      color:
                        rank === QuestionAnswerRank.Neutral || rank === QuestionAnswerRank.Negative
                          ? alpha(palette.common.black, 0.6)
                          : palette.common.white,
                    }}
                  />
                </Avatar>
              ))}
            </AvatarGroup>
          </Tooltip>
        )}
        {item?.isScraped === true && <AutoAwesomeOutlined sx={{ color: palette.communication.dark }} />}
        <QuestionItemAnswer item={item} maxRows={2} responderName={item.creatorOrganization?.name} />
      </Stack>
    </ParentCellOrFragment>
  )
}

export default AnswerWithClassificationCell
